import { useMemo, useState } from 'react';
import { CalendarDays, CheckCircle2, Clock, ListChecks, User as UserIcon } from 'lucide-react';
import { WrappingCellText } from '@/components/common/DataTable';
import { EmptyState } from '@/components/common/EmptyState';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TABLE_HEADER_CLASS } from '@/styles/dataTableBg';
import { formatDisplayDate } from '@/utils/date';
import { formatLocation } from '@/utils/formatLocation';
import { getInitialsFromName } from '@/utils/getInitialsFromName';
import { formatCount } from '@/utils/number';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import {
  SURVEY_QUESTION_TYPE_LABELS,
  SURVEY_RESPONDENT_VIEW_MODE,
  SURVEY_RESPONDENT_VIEW_MODE_LABELS,
  type SurveyRespondentViewMode,
} from '../constants/survey.constants';
import {
  QuestionType,
  type SurveyAnswerDetail,
  type SurveyQuestionType,
  type SurveyRespondent,
  type SurveyUserSubmission,
} from '../types/survey.types';

const SURVEY_NUMBER_MARK_CLASS =
  'inline-flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold';

interface QuestionEntry {
  submissionId: number;
  submissionDate: string;
  isComplete: boolean;
  answer: SurveyAnswerDetail;
}

interface QuestionGroup {
  questionId: number;
  questionText: string;
  questionType: SurveyQuestionType;
  sortOrder: number;
  entries: QuestionEntry[];
}

interface SurveyRespondentDetailModalProps {
  respondent: SurveyRespondent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function sortSubmissionsDesc(submissions: SurveyUserSubmission[]): SurveyUserSubmission[] {
  return [...submissions].sort((a, b) => b.submissionDate.localeCompare(a.submissionDate));
}

function sortAnswersByOrder(answers: SurveyAnswerDetail[]): SurveyAnswerDetail[] {
  return [...answers].sort((a, b) => a.sortOrder - b.sortOrder);
}

function groupBySortedQuestions(submissions: SurveyUserSubmission[]): QuestionGroup[] {
  const map = new Map<number, QuestionGroup>();
  for (const submission of submissions) {
    for (const answer of submission.answers) {
      let group = map.get(answer.questionId);
      if (!group) {
        group = {
          questionId: answer.questionId,
          questionText: answer.questionText,
          questionType: answer.questionType,
          sortOrder: answer.sortOrder,
          entries: [],
        };
        map.set(answer.questionId, group);
      }
      group.entries.push({
        submissionId: submission.id,
        submissionDate: submission.submissionDate,
        isComplete: submission.isComplete,
        answer,
      });
    }
  }

  const groups = Array.from(map.values()).sort((a, b) => a.sortOrder - b.sortOrder);
  for (const group of groups) {
    group.entries.sort((a, b) => b.submissionDate.localeCompare(a.submissionDate));
  }
  return groups;
}

function AnswerCell({ answer }: { answer: SurveyAnswerDetail }) {
  const { questionType, answerValue, selectedOptions } = answer;

  if (questionType === QuestionType.CHECKBOX) {
    if (selectedOptions.length === 0) {
      return <span className="text-sm text-muted-foreground">No selection</span>;
    }
    return (
      <div className="flex flex-wrap gap-1.5">
        {selectedOptions.map((option) => (
          <Badge key={option.id} variant="secondary" className="font-normal">
            {option.text}
          </Badge>
        ))}
      </div>
    );
  }

  if (questionType === QuestionType.MCQ || questionType === QuestionType.DROPDOWN) {
    if (selectedOptions.length === 0) {
      return <span className="text-sm text-muted-foreground">No selection</span>;
    }
    return (
      <Badge variant="secondary" className="font-normal">
        {selectedOptions[0]!.text}
      </Badge>
    );
  }

  if (!answerValue || answerValue.trim() === '') {
    return <span className="text-sm text-muted-foreground">No answer</span>;
  }

  if (questionType === QuestionType.RATING) {
    return (
      <span className="text-base font-semibold tabular-nums text-foreground">{answerValue}</span>
    );
  }

  return (
    <p className="text-sm whitespace-pre-line text-foreground wrap-break-word">{answerValue}</p>
  );
}

interface CompletionBadgeProps {
  isComplete: boolean;
}

function CompletionBadge({ isComplete }: CompletionBadgeProps) {
  const Icon = isComplete ? CheckCircle2 : Clock;
  return (
    <StatusBadge tone={isComplete ? 'success' : 'warning'}>
      <Icon aria-hidden />
      {isComplete ? 'Complete' : 'Incomplete'}
    </StatusBadge>
  );
}

function SubmissionSection({
  submission,
  index,
}: {
  submission: SurveyUserSubmission;
  index: number;
}) {
  const answers = sortAnswersByOrder(submission.answers);

  return (
    <section
      className="flex flex-col gap-2.5"
      aria-labelledby={`submission-${submission.id}-heading`}
    >
      <div className="sticky top-0 z-20 -mx-6 flex flex-wrap items-center justify-between gap-2 border-b bg-card px-6 py-3 shadow-sm">
        <div className="flex items-center gap-2.5 text-sm">
          <span className={cn(SURVEY_NUMBER_MARK_CLASS, ACCENT_MARK_CLASS)}>{index + 1}</span>
          <h3 id={`submission-${submission.id}-heading`} className="font-semibold">
            {formatDisplayDate(submission.submissionDate)}
          </h3>
          <span className="text-muted-foreground">· {formatCount(answers.length, 'answer')}</span>
        </div>
        <CompletionBadge isComplete={submission.isComplete} />
      </div>

      {answers.length === 0 ? (
        <EmptyState title="No answers recorded" size="compact" />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <colgroup>
              <col className="w-12" />
              <col />
              <col className="w-32" />
              <col className="w-2/5" />
            </colgroup>
            <TableHeader className={TABLE_HEADER_CLASS}>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-center">#</TableHead>
                <TableHead>Question</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Answer</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {answers.map((answer, answerIndex) => (
                <TableRow key={answer.questionId}>
                  <TableCell className="text-center align-top text-sm font-medium text-muted-foreground tabular-nums">
                    {answerIndex + 1}
                  </TableCell>
                  <TableCell className="align-top text-sm font-medium">
                    <WrappingCellText width="fill">{answer.questionText}</WrappingCellText>
                  </TableCell>
                  <TableCell className="align-top">
                    <Badge variant="outline" className="font-normal">
                      {SURVEY_QUESTION_TYPE_LABELS[answer.questionType]}
                    </Badge>
                  </TableCell>
                  <TableCell className="align-top wrap-break-word">
                    <AnswerCell answer={answer} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

function QuestionGroupSection({ group, index }: { group: QuestionGroup; index: number }) {
  return (
    <section
      className="flex flex-col gap-2.5"
      aria-labelledby={`question-${group.questionId}-heading`}
    >
      <div className="sticky top-0 z-20 -mx-6 flex flex-wrap items-start justify-between gap-x-3 gap-y-2 border-b bg-card px-6 py-3 shadow-sm">
        <div className="flex min-w-0 flex-1 items-start gap-2.5 text-sm">
          <span className={cn('mt-0.5', SURVEY_NUMBER_MARK_CLASS, ACCENT_MARK_CLASS)}>
            {index + 1}
          </span>
          <h3
            id={`question-${group.questionId}-heading`}
            className="min-w-0 flex-1 text-sm font-semibold leading-snug wrap-break-word"
          >
            {group.questionText}
          </h3>
        </div>
        <Badge variant="outline" className="mt-0.5 font-normal">
          {SURVEY_QUESTION_TYPE_LABELS[group.questionType]}
        </Badge>
      </div>

      {group.entries.length === 0 ? (
        <EmptyState title="No answers recorded" size="compact" />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <colgroup>
              <col className="w-12" />
              <col className="w-36" />
              <col className="w-32" />
              <col />
            </colgroup>
            <TableHeader className={TABLE_HEADER_CLASS}>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-center">#</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Answer</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {group.entries.map((entry, entryIndex) => (
                <TableRow key={entry.submissionId}>
                  <TableCell className="text-center align-top text-sm font-medium text-muted-foreground tabular-nums">
                    {entryIndex + 1}
                  </TableCell>
                  <TableCell className="align-top text-sm font-medium whitespace-nowrap">
                    {formatDisplayDate(entry.submissionDate)}
                  </TableCell>
                  <TableCell className="align-top">
                    <CompletionBadge isComplete={entry.isComplete} />
                  </TableCell>
                  <TableCell className="align-top wrap-break-word">
                    <AnswerCell answer={entry.answer} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

function ViewModeToggle({
  value,
  onChange,
}: {
  value: SurveyRespondentViewMode;
  onChange: (next: SurveyRespondentViewMode) => void;
}) {
  return (
    <div
      className="inline-flex items-center gap-0.5 rounded-md border bg-muted/40 p-0.5"
      role="group"
      aria-label="Submission view mode"
    >
      <Button
        type="button"
        size="sm"
        variant={value === SURVEY_RESPONDENT_VIEW_MODE.BY_DAY ? 'secondary' : 'ghost'}
        onClick={() => onChange(SURVEY_RESPONDENT_VIEW_MODE.BY_DAY)}
        aria-pressed={value === SURVEY_RESPONDENT_VIEW_MODE.BY_DAY}
        className="h-7 gap-1.5 px-2.5 text-xs shadow-none"
      >
        <CalendarDays className="size-3.5" aria-hidden />
        {SURVEY_RESPONDENT_VIEW_MODE_LABELS[SURVEY_RESPONDENT_VIEW_MODE.BY_DAY]}
      </Button>
      <Button
        type="button"
        size="sm"
        variant={value === SURVEY_RESPONDENT_VIEW_MODE.BY_QUESTION ? 'secondary' : 'ghost'}
        onClick={() => onChange(SURVEY_RESPONDENT_VIEW_MODE.BY_QUESTION)}
        aria-pressed={value === SURVEY_RESPONDENT_VIEW_MODE.BY_QUESTION}
        className="h-7 gap-1.5 px-2.5 text-xs shadow-none"
      >
        <ListChecks className="size-3.5" aria-hidden />
        {SURVEY_RESPONDENT_VIEW_MODE_LABELS[SURVEY_RESPONDENT_VIEW_MODE.BY_QUESTION]}
      </Button>
    </div>
  );
}

export function SurveyRespondentDetailModal({
  respondent,
  open,
  onOpenChange,
}: SurveyRespondentDetailModalProps) {
  const [viewMode, setViewMode] = useState<SurveyRespondentViewMode>(
    SURVEY_RESPONDENT_VIEW_MODE.BY_DAY,
  );
  const submissions = useMemo(
    () => (respondent ? sortSubmissionsDesc(respondent.submissions) : []),
    [respondent],
  );
  const questionGroups = useMemo(() => groupBySortedQuestions(submissions), [submissions]);
  const submissionCount = submissions.length;
  const questionCount = questionGroups.length;
  const location = respondent ? formatLocation(respondent) : null;

  const summaryLabel =
    viewMode === SURVEY_RESPONDENT_VIEW_MODE.BY_DAY
      ? formatCount(submissionCount, 'submission')
      : formatCount(questionCount, 'question');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-screen w-screen max-w-none flex-col overflow-hidden rounded-none p-0 sm:h-[85vh] sm:w-[90vw] sm:max-w-6xl sm:rounded-xl">
        {respondent ? (
          <>
            <DialogHeader className="shrink-0 border-b px-6 pt-6 pb-4">
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    'flex size-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                    ACCENT_MARK_CLASS,
                  )}
                  aria-hidden
                >
                  {respondent.userName ? (
                    getInitialsFromName(respondent.userName)
                  ) : (
                    <UserIcon className="size-5" />
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <DialogTitle className="truncate text-lg leading-tight">
                    {respondent.userName}
                  </DialogTitle>
                  <DialogDescription className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <span className="inline-flex items-center gap-1">
                      <span className="text-muted-foreground">HR ID</span>
                      <span className="font-medium text-foreground">{respondent.userHrId}</span>
                    </span>
                    {respondent.contactNumber ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="text-muted-foreground">Contact</span>
                        <span className="font-medium text-foreground">
                          {respondent.contactNumber}
                        </span>
                      </span>
                    ) : null}
                    {respondent.departmentName ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="text-muted-foreground">Department</span>
                        <span className="font-medium text-foreground">
                          {respondent.departmentName}
                        </span>
                      </span>
                    ) : null}
                    {location ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="text-muted-foreground">Location</span>
                        <span className="font-medium text-foreground">{location}</span>
                      </span>
                    ) : null}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6">
              {submissionCount === 0 ? (
                <EmptyState title="No submissions recorded" className="mt-5" />
              ) : (
                <div className="flex flex-col gap-6 pb-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 pt-5">
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-semibold">{summaryLabel}</h2>
                    </div>
                    <ViewModeToggle value={viewMode} onChange={setViewMode} />
                  </div>

                  {viewMode === SURVEY_RESPONDENT_VIEW_MODE.BY_DAY
                    ? submissions.map((submission, index) => (
                        <SubmissionSection
                          key={submission.id}
                          submission={submission}
                          index={index}
                        />
                      ))
                    : questionGroups.map((group, index) => (
                        <QuestionGroupSection key={group.questionId} group={group} index={index} />
                      ))}
                </div>
              )}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
